import React, { useState, useMemo } from 'react';
import {
  Search, Users, Award, XCircle, Clock,
  ChevronRight, AlertCircle, Filter, ArrowUpRight,
  Mail, Phone, FileText, Check, X, MoreHorizontal,
  Calendar, Briefcase, Star, Download, Eye
} from 'lucide-react';
import { APP, STATUS_COLORS, StatusBadge, IconPill } from './DashbordApplicationTab';

/* ─── Helpers ────────────────────────────────────────────────────────────── */
const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

const daysAgo = (d) => {
  if (!d) return null;
  const diff = Math.floor((Date.now() - new Date(d)) / 86_400_000);
  if (diff === 0) return 'Today';
  if (diff === 1) return '1d ago';
  return `${diff}d ago`;
};

const initials = (name) =>
  name
    ? name.split(' ').map((n) => n[0]).slice(0, 2).join('').toUpperCase()
    : '?';

/* avatar colour cycles through a palette based on first char */
const AVATAR_PALETTES = [
  { bg: APP.primaryBg, color: APP.primary   },
  { bg: APP.purpleBg,  color: APP.purple    },
  { bg: APP.greenBg,   color: APP.green     },
  { bg: APP.amberBg,   color: APP.amber     },
  { bg: APP.skyBg,     color: APP.sky       },
  { bg: APP.pinkBg,    color: APP.pink      },
];
const palette = (name) =>
  AVATAR_PALETTES[(name?.charCodeAt(0) ?? 0) % AVATAR_PALETTES.length];

const Avatar = ({ name, size = 'md' }) => {
  const { bg, color } = palette(name);
  const sz = size === 'lg' ? 'w-12 h-12 text-base' : 'w-9 h-9 text-sm';
  return (
    <div
      style={{ backgroundColor: bg, color }}
      className={`${sz} rounded-full font-bold flex items-center justify-center flex-shrink-0`}
    >
      {initials(name)}
    </div>
  );
};

/* ─── KPI summary cards ──────────────────────────────────────────────────── */
const CandidateKPICards = ({ candidates }) => {
  const total    = candidates.length;
  const accepted = candidates.filter((c) => c.status === 'accepted').length;
  const rejected = candidates.filter((c) => c.status === 'rejected').length;
  const pending  = candidates.filter((c) => c.status === 'pending').length;

  const CARDS = [
    { label: 'Total candidates', value: total,    sub: 'All applicants',      icon: Users,   bg: APP.primaryBg, color: APP.primary },
    { label: 'Accepted',         value: accepted, sub: `${total ? ((accepted/total)*100).toFixed(0) : 0}% acceptance rate`, icon: Award,   bg: APP.greenBg,   color: APP.green   },
    { label: 'Pending review',   value: pending,  sub: 'Awaiting decision',   icon: Clock,   bg: APP.amberBg,   color: APP.amber   },
    { label: 'Rejected',         value: rejected, sub: `${total ? ((rejected/total)*100).toFixed(0) : 0}% rejection rate`,  icon: XCircle, bg: APP.redBg,     color: APP.red     },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
      {CARDS.map(({ label, value, sub, icon, bg, color }) => (
        <div
          key={label}
          style={{ backgroundColor: APP.card, border: `1px solid ${APP.border}` }}
          className="rounded-2xl p-4 flex flex-col gap-3 hover:shadow-sm transition-shadow"
        >
          <IconPill icon={icon} bg={bg} color={color} />
          <div>
            <p style={{ color: APP.text }} className="text-2xl font-bold leading-none">{value}</p>
            <p style={{ color: APP.textSm }} className="text-xs mt-1">{label}</p>
          </div>
          <p style={{ color: APP.textXs }} className="text-xs">{sub}</p>
        </div>
      ))}
    </div>
  );
};

/* ─── Candidate detail drawer ────────────────────────────────────────────── */
const CandidateDrawer = ({ candidate, onClose, onAction }) => {
  if (!candidate) return null;

  return (
    <>
    <style>{`
      @keyframes slideIn {
        from { transform: translateX(100%); }
        to   { transform: translateX(0); }
      }
      @keyframes fadeIn {
        from { opacity: 0; }
        to   { opacity: 1; }
      }
    `}</style>

      {/* Backdrop */}
      <div
  style={{ animation: 'fadeIn 0.2s ease-in-out' }}
  className="fixed inset-0 z-[999] bg-black/20 backdrop-blur-[1px]"
  onClick={onClose}
/>

      {/* Drawer panel */}
      <div
  style={{
    backgroundColor: APP.card,
    borderLeft: `1px solid ${APP.border}`,
    animation: 'slideIn 0.3s ease-in-out',
  }}
  className="fixed right-0 top-20 h-full w-full max-w-sm z-[1000] flex flex-col shadow-2xl overflow-y-auto"
>
        {/* Header */}
        <div
          style={{ borderBottom: `1px solid ${APP.border}` }}
          className="flex items-center justify-between px-5 py-4 flex-shrink-0"
        >
          <h3 style={{ color: APP.text }} className="text-sm font-semibold">Candidate profile</h3>
          <button
            onClick={onClose}
            style={{ backgroundColor: APP.bg, color: APP.textSm }}
            className="w-7 h-9 rounded-lg flex items-center justify-center hover:bg-slate-100 transition-colors"
          >
            <X size={14} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 px-5 py-5 space-y-5">

          {/* Avatar + name */}
          <div className="flex items-center gap-4">
            <Avatar name={candidate.candidateName} size="lg" />
            <div>
              <p style={{ color: APP.text }} className="text-base font-bold">
                {candidate.candidateName}
              </p>
              <p style={{ color: APP.textSm }} className="text-xs mt-0.5">{candidate.jobTitle}</p>
              <div className="mt-1.5">
                <StatusBadge status={candidate.status} />
              </div>
            </div>
          </div>

          {/* Info rows */}
          <div
            style={{ backgroundColor: APP.bg, border: `1px solid ${APP.border}` }}
            className="rounded-xl divide-y"
            style2={{ borderColor: APP.borderLight }}
          >
            {[
              { icon: Briefcase, label: 'Applied for',  value: candidate.jobTitle   },
              { icon: Calendar,  label: 'Applied on',   value: fmtDate(candidate.date) },
              { icon: Clock,     label: 'Status',       value: candidate.status, isStatus: true },
            ].map(({ icon: Icon, label, value, isStatus }) => (
              <div
                key={label}
                style={{ borderColor: APP.borderLight }}
                className="flex items-center gap-3 px-4 py-3"
              >
                <Icon size={13} style={{ color: APP.textXs }} className="flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p style={{ color: APP.textXs }} className="text-[10px] uppercase tracking-wide font-semibold">{label}</p>
                  {isStatus
                    ? <div className="mt-0.5"><StatusBadge status={value} /></div>
                    : <p style={{ color: APP.text }} className="text-xs font-medium mt-0.5 truncate">{value}</p>
                  }
                </div>
              </div>
            ))}
          </div>

          {/* Action buttons */}
          {candidate.status === 'pending' && (
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => { onAction(candidate.id, 'accepted'); onClose(); }}
                style={{ backgroundColor: APP.greenBg, color: APP.green, border: `1px solid ${APP.green}33` }}
                className="flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-semibold hover:opacity-80 transition-opacity"
              >
                <Check size={13} /> Accept
              </button>
              <button
                onClick={() => { onAction(candidate.id, 'rejected'); onClose(); }}
                style={{ backgroundColor: APP.redBg, color: APP.red, border: `1px solid ${APP.red}33` }}
                className="flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-semibold hover:opacity-80 transition-opacity"
              >
                <X size={13} /> Reject
              </button>
            </div>
          )}

          {candidate.status === 'accepted' && (
            <div
              style={{ backgroundColor: APP.greenBg, border: `1px solid ${APP.green}33` }}
              className="rounded-xl px-4 py-3 flex items-center gap-2"
            >
              <Check size={14} style={{ color: APP.green }} />
              <p style={{ color: APP.green }} className="text-xs font-semibold">
                Offer has been extended to this candidate
              </p>
            </div>
          )}

          {candidate.status === 'rejected' && (
            <div
              style={{ backgroundColor: APP.redBg, border: `1px solid ${APP.red}33` }}
              className="rounded-xl px-4 py-3 flex items-center gap-2"
            >
              <X size={14} style={{ color: APP.red }} />
              <p style={{ color: APP.red }} className="text-xs font-semibold">
                This candidate was not selected
              </p>
            </div>
          )}

          {/* Resume / contact placeholder */}
          <div
            style={{ backgroundColor: APP.bg, border: `1px solid ${APP.border}` }}
            className="rounded-xl px-4 py-3 flex items-center justify-between"
          >
            <div className="flex items-center gap-2">
              <FileText size={14} style={{ color: APP.textXs }} />
              <p style={{ color: APP.textSm }} className="text-xs">Resume / CV</p>
            </div>
            <button
              style={{ color: APP.primary }}
              className="text-xs font-semibold flex items-center gap-1 hover:underline"
            >
              <Eye size={11} /> View
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

/* ─── Candidate row (list view) ──────────────────────────────────────────── */
const CandidateRow = ({ candidate, onSelect, onAction }) => (
  <div
    style={{ borderBottom: `1px solid ${APP.borderLight}` }}
    className="flex items-center gap-3 py-3 px-4 hover:bg-slate-50 transition-colors cursor-pointer group"
    onClick={() => onSelect(candidate)}
  >
    <Avatar name={candidate.candidateName} />

    <div className="flex-1 min-w-0">
      <p style={{ color: APP.text }} className="text-sm font-medium truncate">
        {candidate.candidateName}
      </p>
      <p style={{ color: APP.textSm }} className="text-xs truncate">{candidate.jobTitle}</p>
    </div>

    <div className="hidden md:flex items-center gap-1 text-xs min-w-[70px]" style={{ color: APP.textXs }}>
      <Calendar size={11} />
      <span>{daysAgo(candidate.date)}</span>
    </div>

    <StatusBadge status={candidate.status} />

    {/* Quick actions — show on hover */}
    <div
      className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity"
      onClick={(e) => e.stopPropagation()}
    >
      {candidate.status === 'pending' && (
        <>
          <button
            onClick={() => onAction(candidate.id, 'accepted')}
            title="Accept"
            style={{ backgroundColor: APP.greenBg, color: APP.green }}
            className="w-7 h-7 rounded-lg flex items-center justify-center hover:opacity-80 transition-opacity"
          >
            <Check size={12} />
          </button>
          <button
            onClick={() => onAction(candidate.id, 'rejected')}
            title="Reject"
            style={{ backgroundColor: APP.redBg, color: APP.red }}
            className="w-7 h-7 rounded-lg flex items-center justify-center hover:opacity-80 transition-opacity"
          >
            <X size={12} />
          </button>
        </>
      )}
      <button
        onClick={() => onSelect(candidate)}
        title="View profile"
        style={{ backgroundColor: APP.primaryBg, color: APP.primary }}
        className="w-7 h-7 rounded-lg flex items-center justify-center hover:opacity-80 transition-opacity"
      >
        <Eye size={12} />
      </button>
    </div>
  </div>
);

/* ─── Candidate card (grid view) ─────────────────────────────────────────── */
const CandidateCard = ({ candidate, onSelect, onAction }) => (
  <div
    style={{ backgroundColor: APP.card, border: `1px solid ${APP.border}` }}
    className="rounded-2xl p-4 flex flex-col gap-3 hover:shadow-sm transition-shadow cursor-pointer group"
    onClick={() => onSelect(candidate)}
  >
    <div className="flex items-start justify-between">
      <Avatar name={candidate.candidateName} />
      <StatusBadge status={candidate.status} />
    </div>

    <div>
      <p style={{ color: APP.text }} className="text-sm font-semibold truncate">
        {candidate.candidateName}
      </p>
      <p style={{ color: APP.textSm }} className="text-xs truncate mt-0.5">
        {candidate.jobTitle}
      </p>
      <p style={{ color: APP.textXs }} className="text-[10px] mt-1 flex items-center gap-1">
        <Calendar size={9} /> {fmtDate(candidate.date)}
      </p>
    </div>

    <div
      className="flex items-center gap-2 pt-2"
      style={{ borderTop: `1px solid ${APP.borderLight}` }}
      onClick={(e) => e.stopPropagation()}
    >
      {candidate.status === 'pending' ? (
        <>
          <button
            onClick={() => onAction(candidate.id, 'accepted')}
            style={{ backgroundColor: APP.greenBg, color: APP.green }}
            className="flex-1 flex items-center justify-center gap-1 py-1.5 rounded-lg text-[11px] font-semibold hover:opacity-80 transition-opacity"
          >
            <Check size={11} /> Accept
          </button>
          <button
            onClick={() => onAction(candidate.id, 'rejected')}
            style={{ backgroundColor: APP.redBg, color: APP.red }}
            className="flex-1 flex items-center justify-center gap-1 py-1.5 rounded-lg text-[11px] font-semibold hover:opacity-80 transition-opacity"
          >
            <X size={11} /> Reject
          </button>
        </>
      ) : (
        <button
          onClick={() => onSelect(candidate)}
          style={{ color: APP.primary }}
          className="text-xs font-semibold flex items-center gap-1 hover:underline"
        >
          View profile <ChevronRight size={11} />
        </button>
      )}
    </div>
  </div>
);

/* ─── Empty state ────────────────────────────────────────────────────────── */
const EmptyCandidates = () => (
  <div className="flex flex-col items-center gap-3 py-16">
    <div
      style={{ backgroundColor: APP.primaryBg }}
      className="w-14 h-14 rounded-2xl flex items-center justify-center"
    >
      <Users size={22} style={{ color: APP.primary }} />
    </div>
    <p style={{ color: APP.text }} className="text-sm font-semibold">No candidates found</p>
    <p style={{ color: APP.textXs }} className="text-xs text-center max-w-xs">
      Try adjusting your search or filters to find candidates.
    </p>
  </div>
);

/* ─── Main CandidatesTab ─────────────────────────────────────────────────── */
const CandidatesTab = ({ dashboardData }) => {
  const [view, setView]               = useState('list');   // 'list' | 'grid'
  const [search, setSearch]           = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy, setSortBy]           = useState('newest');
  const [selected, setSelected]       = useState(null);
  const [localCandidates, setLocalCandidates] = useState(null);

  const raw = localCandidates ?? dashboardData?.recentApplications ?? [];

  /* Filter + sort */
  const candidates = useMemo(() => {
    let list = raw.filter((c) => {
      const matchSearch =
        !search ||
        c.candidateName?.toLowerCase().includes(search.toLowerCase()) ||
        c.jobTitle?.toLowerCase().includes(search.toLowerCase());
      const matchStatus = filterStatus === 'all' || c.status === filterStatus;
      return matchSearch && matchStatus;
    });

    if (sortBy === 'name')    list = [...list].sort((a, b) => a.candidateName?.localeCompare(b.candidateName));
    if (sortBy === 'status')  list = [...list].sort((a, b) => a.status?.localeCompare(b.status));
    // newest = default API order

    return list;
  }, [raw, search, filterStatus, sortBy]);

  /* Optimistic action */
  const handleAction = (id, newStatus) => {
    setLocalCandidates((prev) =>
      (prev ?? raw).map((c) => (c.id === id ? { ...c, status: newStatus } : c))
    );
    // Also update drawer if open
    if (selected?.id === id) setSelected((s) => ({ ...s, status: newStatus }));
  };

  const STATUS_FILTERS = ['all', 'pending', 'accepted', 'rejected'];

  return (
    <div>
      {/* Drawer */}
      <CandidateDrawer
        candidate={selected}
        onClose={() => setSelected(null)}
        onAction={handleAction}
      />

      {/* Section header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
        <div>
          <h2 style={{ color: APP.text }} className="text-base font-semibold">Candidates</h2>
          <p style={{ color: APP.textXs }} className="text-xs mt-0.5">
            {raw.length} candidate{raw.length !== 1 ? 's' : ''} across all job postings
          </p>
        </div>

        {/* View toggle */}
        <div style={{ border: `1px solid ${APP.border}` }} className="flex rounded-xl overflow-hidden self-start">
          {[
            { key: 'list', label: 'List' },
            { key: 'grid', label: 'Grid' },
          ].map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setView(key)}
              style={
                view === key
                  ? { backgroundColor: APP.primary, color: '#fff' }
                  : { backgroundColor: APP.card, color: APP.textMd }
              }
              className="px-4 py-1.5 text-xs font-medium hover:opacity-90 transition-opacity"
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* KPI cards */}
      <CandidateKPICards candidates={raw} />

      {/* Filter bar */}
      <div
        style={{ backgroundColor: APP.card, border: `1px solid ${APP.border}` }}
        className="rounded-2xl px-4 py-3 flex flex-col sm:flex-row gap-3 items-stretch sm:items-center mb-5"
      >
        {/* Search */}
        <div className="relative flex-1 max-w-xs">
          <Search
            size={13}
            className="absolute left-3 top-1/2 -translate-y-1/2"
            style={{ color: APP.textXs }}
          />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or job…"
            style={{
              backgroundColor: APP.bg,
              border: `1px solid ${APP.border}`,
              color: APP.text,
            }}
            className="w-full pl-8 pr-3 py-2 text-xs rounded-xl outline-none focus:ring-2 focus:ring-blue-100"
          />
        </div>

        {/* Status filters */}
        <div className="flex gap-1.5 flex-wrap">
          {STATUS_FILTERS.map((s) => {
            const active = filterStatus === s;
            const color  = s === 'all' ? APP.primary : STATUS_COLORS[s];
            const bg     = s === 'all' ? APP.primaryBg
                         : s === 'accepted' ? APP.greenBg
                         : s === 'rejected' ? APP.redBg
                         : APP.amberBg;
            return (
              <button
                key={s}
                onClick={() => setFilterStatus(s)}
                style={
                  active
                    ? { backgroundColor: bg, color, border: `1px solid ${color}33` }
                    : { backgroundColor: APP.bg, color: APP.textMd, border: `1px solid ${APP.border}` }
                }
                className="px-3 py-1.5 text-xs font-medium rounded-lg capitalize hover:opacity-90 transition-opacity"
              >
                {s === 'all' ? 'All' : s}
              </button>
            );
          })}
        </div>

        {/* Sort */}
        <div className="flex gap-1.5 sm:ml-auto flex-wrap">
          {[
            { key: 'newest', label: 'Newest' },
            { key: 'name',   label: 'A → Z'  },
            { key: 'status', label: 'Status' },
          ].map(({ key, label }) => {
            const active = sortBy === key;
            return (
              <button
                key={key}
                onClick={() => setSortBy(key)}
                style={
                  active
                    ? { backgroundColor: APP.primaryBg, color: APP.primary, border: `1px solid ${APP.primary}22` }
                    : { backgroundColor: APP.bg, color: APP.textMd, border: `1px solid ${APP.border}` }
                }
                className="px-3 py-1.5 text-xs font-medium rounded-lg hover:opacity-90 transition-opacity"
              >
                {label}
              </button>
            );
          })}
        </div>
      </div>

      {/* ── List view ── */}
      {view === 'list' && (
        <div
          style={{ backgroundColor: APP.card, border: `1px solid ${APP.border}` }}
          className="rounded-2xl overflow-hidden"
        >
          {/* Table head */}
          <div
            className="flex items-center gap-3 px-4 py-2.5 text-[10px] font-semibold uppercase tracking-wider"
            style={{
              color: APP.textXs,
              borderBottom: `1px solid ${APP.border}`,
              backgroundColor: APP.bg,
            }}
          >
            <span className="w-9 flex-shrink-0" />
            <span className="flex-1">Candidate / Role</span>
            <span className="hidden md:block w-20">Applied</span>
            <span className="w-20">Status</span>
            <span className="w-24 text-right">Actions</span>
          </div>

          {candidates.length === 0
            ? <EmptyCandidates />
            : candidates.map((c) => (
                <CandidateRow
                  key={c.id}
                  candidate={c}
                  onSelect={setSelected}
                  onAction={handleAction}
                />
              ))
          }
        </div>
      )}

      {/* ── Grid view ── */}
      {view === 'grid' && (
        candidates.length === 0
          ? <EmptyCandidates />
          : (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
              {candidates.map((c) => (
                <CandidateCard
                  key={c.id}
                  candidate={c}
                  onSelect={setSelected}
                  onAction={handleAction}
                />
              ))}
            </div>
          )
      )}

      {/* Pending nudge banner */}
      {raw.filter((c) => c.status === 'pending').length > 0 && (
        <div
          style={{ backgroundColor: APP.amberBg, border: `1px solid ${APP.amber}33` }}
          className="rounded-2xl px-4 py-3 mt-5 flex items-center gap-3"
        >
          <Clock size={15} style={{ color: APP.amber }} />
          <p style={{ color: APP.amber }} className="text-xs font-medium flex-1">
            {raw.filter((c) => c.status === 'pending').length} candidate
            {raw.filter((c) => c.status === 'pending').length !== 1 ? 's are' : ' is'} still
            pending — review them before they move on.
          </p>
          <button
            onClick={() => setFilterStatus('pending')}
            style={{ color: APP.amber }}
            className="text-xs font-semibold flex items-center gap-1 hover:underline whitespace-nowrap"
          >
            Review now <ChevronRight size={12} />
          </button>
        </div>
      )}
    </div>
  );
};

export default CandidatesTab;